﻿// PDFViewer.js
import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  StatusBar,
  Alert,
  Dimensions,
  Platform,
  ActivityIndicator,
  ScrollView,
  TextInput,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { WebView } from 'react-native-webview';
// Native PDF renderer — only present in dev/production builds, not Expo Go.
// Loaded defensively so the screen still works (via WebView fallback) if absent.
let Pdf = null;
try { Pdf = require('react-native-pdf').default; } catch (e) { Pdf = null; }
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import * as Linking from 'expo-linking';
import * as DocumentPicker from 'expo-document-picker';

const { width, height } = Dimensions.get('window');

const PDFViewer = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { book, url, title } = route.params || {};
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [darkMode, setDarkMode] = useState(false); // Initialized with false
  const [showControls, setShowControls] = useState(true);
  const [bookmarkedPages, setBookmarkedPages] = useState([]);
  const [notes, setNotes] = useState({});
  const [currentNote, setCurrentNote] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [pdfUri, setPdfUri] = useState(null);
  const [directPdfUrl, setDirectPdfUrl] = useState(null);
  const [pdfPage, setPdfPage] = useState(1);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [isLocalFile, setIsLocalFile] = useState(false);
  
  const webViewRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Use a local variable for safe access to darkMode
  const isDarkMode = darkMode ?? false;

  useEffect(() => {
    loadInitialData();
    hideControlsAfterDelay();
    loadDarkModePreference();
    loadBookmarks();
    loadNotes();
    setupPdfSource();
    
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [book, url]);

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        const parsedValue = JSON.parse(darkModePref);
        setDarkMode(parsedValue);
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
      setDarkMode(false);
    }
  };

  const loadBookmarks = async () => {
    try {
      const bookmarks = await AsyncStorage.getItem(`bookmarks_${book?.id || title}`);
      if (bookmarks) {
        setBookmarkedPages(JSON.parse(bookmarks));
      }
    } catch (error) {
      console.error('Error loading bookmarks:', error);
    }
  };

  const loadNotes = async () => {
    try {
      const storedNotes = await AsyncStorage.getItem(`notes_${book?.id || title}`);
      if (storedNotes) {
        setNotes(JSON.parse(storedNotes));
      }
    } catch (error) {
      console.error('Error loading notes:', error);
    }
  };

  const setupPdfSource = () => {
    try {
      // Only treat genuine PDF sources as PDFs. Never feed an Amazon product
      // link or a random sample into the viewer — that caused an infinite spinner.
      const pdfUrl = url || book?.pdfUrl || book?.pdf_url;

      if (!pdfUrl) {
        setPdfUri(null);
        setDirectPdfUrl(null);
        setLoading(false);
        setError('No readable copy is available for this book yet. Use "Buy on Amazon" on the previous screen to purchase it.');
        return;
      }

      const isPdf = /\.pdf($|\?)/i.test(pdfUrl) || /^https?:\/\//i.test(pdfUrl);
      if (!isPdf) {
        setPdfUri(null);
        setDirectPdfUrl(null);
        setLoading(false);
        setError('This document cannot be displayed.');
        return;
      }

      // Raw URL for the native react-native-pdf renderer (handles large files).
      setDirectPdfUrl(pdfUrl);
      // Google Docs viewer URL kept as a fallback for Expo Go / no native module.
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(pdfUrl)}&embedded=true`;
      setPdfUri(viewerUrl);
      setIsLocalFile(false);
    } catch (error) {
      console.error('Error setting up PDF source:', error);
      setLoading(false);
      setError('Failed to load PDF document');
    }
  };

  const loadInitialData = () => {
    setLoading(true);
    setError(null);
  };

  const hideControlsAfterDelay = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const toggleControls = () => {
    setShowControls(!showControls);
    if (!showControls) {
      hideControlsAfterDelay();
    }
  };

  const toggleBookmark = async () => {
    try {
      let newBookmarks;
      if (bookmarkedPages.includes(page)) {
        newBookmarks = bookmarkedPages.filter(p => p !== page);
      } else {
        newBookmarks = [...bookmarkedPages, page];
      }
      
      setBookmarkedPages(newBookmarks);
      await AsyncStorage.setItem(
        `bookmarks_${book?.id || title}`,
        JSON.stringify(newBookmarks)
      );
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    }
  };

  const saveNote = async () => {
    if (!currentNote.trim()) return;
    
    try {
      const newNotes = { ...notes, [page]: currentNote };
      setNotes(newNotes);
      setCurrentNote('');
      await AsyncStorage.setItem(
        `notes_${book?.id || title}`,
        JSON.stringify(newNotes)
      );
      Alert.alert('Success', 'Note saved successfully!');
    } catch (error) {
      console.error('Error saving note:', error);
      Alert.alert('Error', 'Failed to save note');
    }
  };

  const deleteNote = async (pageNum) => {
    try {
      const newNotes = { ...notes };
      delete newNotes[pageNum];
      setNotes(newNotes);
      await AsyncStorage.setItem(
        `notes_${book?.id || title}`,
        JSON.stringify(newNotes)
      );
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const sharePDF = async () => {
    try {
      const shareUrl = url || book?.pdfUrl || book?.pdf_url || pdfUri;
      if (shareUrl) {
        await Sharing.shareAsync(shareUrl, {
          mimeType: 'application/pdf',
          dialogTitle: `Share ${book?.title || title || 'PDF Document'}`,
        });
      }
    } catch (error) {
      console.error('Error sharing PDF:', error);
      Alert.alert('Error', 'Failed to share document');
    }
  };

  const downloadPDF = async () => {
    const pdfUrl = url || book?.pdfUrl || book?.pdf_url;
    if (!pdfUrl) {
      Alert.alert('Error', 'No PDF available for download');
      return;
    }

    try {
      setLoading(true);
      
      const downloadDir = `${FileSystem.cacheDirectory}downloads/`;
      const dirInfo = await FileSystem.getInfoAsync(downloadDir);
      if (!dirInfo.exists) {
        await FileSystem.makeDirectoryAsync(downloadDir, { intermediates: true });
      }

      const fileName = `${book?.title || title || 'document'}_${Date.now()}.pdf`;
      const fileUri = `${downloadDir}${fileName}`;
      
      const downloadResult = await FileSystem.downloadAsync(pdfUrl, fileUri);
      
      Alert.alert(
        'Download Complete',
        `PDF saved successfully!`,
        [
          { text: 'OK' },
          { 
            text: 'Open', 
            onPress: () => openLocalPDF(downloadResult.uri)
          },
          { 
            text: 'Share', 
            onPress: () => shareLocalPDF(downloadResult.uri)
          }
        ]
      );
    } catch (error) {
      console.error('Error downloading PDF:', error);
      Alert.alert('Error', 'Failed to download PDF');
    } finally {
      setLoading(false);
    }
  };

  const openLocalPDF = async (fileUri) => {
    try {
      await Sharing.shareAsync(fileUri, {
        mimeType: 'application/pdf',
        dialogTitle: `Open ${book?.title || title || 'PDF Document'}`,
      });
    } catch (error) {
      console.error('Error opening PDF:', error);
      Alert.alert('Error', 'No PDF viewer app found');
    }
  };

  const shareLocalPDF = async (fileUri) => {
    try {
      await Sharing.shareAsync(fileUri, {
        mimeType: 'application/pdf',
        dialogTitle: `Share ${book?.title || title || 'PDF Document'}`,
      });
    } catch (error) {
      console.error('Error sharing PDF:', error);
    }
  };

  const openInBrowser = async () => {
    const pdfUrl = url || book?.pdfUrl || book?.pdf_url;
    if (!pdfUrl) {
      Alert.alert('Error', 'No PDF available');
      return;
    }

    try {
      await Linking.openURL(pdfUrl);
    } catch (error) {
      console.error('Error opening in browser:', error);
    }
  };

  const pickLocalPDF = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (result.type === 'success') {
        const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(result.uri)}&embedded=true`;
        setPdfUri(viewerUrl);
        setIsLocalFile(true);
        setPage(1);
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to pick PDF file');
    }
  };

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber);
      webViewRef.current?.injectJavaScript(`
        window.scrollTo(0, ${(pageNumber - 1) * window.innerHeight});
      `);
    }
  };

  const nextPage = () => {
    if (page < totalPages) {
      goToPage(page + 1);
    }
  };

  const prevPage = () => {
    if (page > 1) {
      goToPage(page - 1);
    }
  };

  const jumpToBookmark = (bookmarkPage) => {
    goToPage(bookmarkPage);
    setShowBookmarks(false);
  };

  const handleWebViewMessage = (event) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (data.type === 'pageCount') {
        setTotalPages(data.count);
      } else if (data.type === 'pageChange') {
        setPage(data.page);
      }
    } catch (error) {
      console.error('Error parsing WebView message:', error);
    }
  };

  const renderLoading = () => (
    <View style={[styles.loadingContainer, { backgroundColor: isDarkMode ? '#1a202c' : '#f0f4f8' }]}>
      <ActivityIndicator size={36} color="#667eea" />
      <Text style={[styles.loadingText, { color: isDarkMode ? '#cbd5e0' : '#1f2937' }]}>
        Loading document...
      </Text>
      <Text style={[styles.loadingSubtext, { color: isDarkMode ? '#a0aec0' : '#6b7280' }]}>
        {book?.title || title || 'Engineering Document'}
      </Text>
    </View>
  );

  const renderError = () => (
    <View style={[styles.errorContainer, { backgroundColor: isDarkMode ? '#1a202c' : '#f0f4f8' }]}>
      <MaterialCommunityIcons name="alert-circle-outline" size={64} color="#ef4444" />
      <Text style={[styles.errorText, { color: isDarkMode ? '#cbd5e0' : '#374151' }]}>
        {error || 'Failed to load document'}
      </Text>
      <TouchableOpacity style={styles.retryButton} onPress={setupPdfSource}>
        <Text style={styles.retryButtonText}>Retry</Text>
      </TouchableOpacity>
      <TouchableOpacity style={styles.pickButton} onPress={pickLocalPDF}>
        <Text style={styles.pickButtonText}>Open Local PDF</Text>
      </TouchableOpacity>
    </View>
  );

  const renderControls = () => {
    if (!showControls) return null;

    return (
      <>
        <LinearGradient
          colors={isDarkMode ? ['rgba(45, 55, 72, 0.95)', 'rgba(74, 85, 104, 0.95)'] : ['rgba(255, 255, 255, 0.95)', 'rgba(255, 255, 255, 0.95)']}
          style={styles.topControls}
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color={isDarkMode ? "#ffffff" : "#1f2937"} />
          </TouchableOpacity>

          <View style={styles.titleContainer}>
            <Text style={[styles.documentTitle, { color: isDarkMode ? "#ffffff" : "#1f2937" }]} numberOfLines={1}>
              {book?.title || title || 'Engineering Document'}
            </Text>
            <Text style={[styles.pageInfo, { color: isDarkMode ? "#a0aec0" : "#6b7280" }]}>
              Page {page} of {totalPages || '...'}
            </Text>
          </View>

          <View style={styles.topRightControls}>
            <TouchableOpacity
              style={styles.iconButton}
              onPress={toggleBookmark}
            >
              <MaterialCommunityIcons
                name={bookmarkedPages.includes(page) ? "bookmark" : "bookmark-outline"}
                size={24}
                color={bookmarkedPages.includes(page) ? "#f59e0b" : (isDarkMode ? "#ffffff" : "#1f2937")}
              />
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.iconButton}
              onPress={sharePDF}
            >
              <MaterialCommunityIcons
                name="share-variant"
                size={24}
                color={isDarkMode ? "#ffffff" : "#1f2937"}
              />
            </TouchableOpacity>
          </View>
        </LinearGradient>

        <LinearGradient
          colors={isDarkMode ? ['rgba(45, 55, 72, 0.95)', 'rgba(74, 85, 104, 0.95)'] : ['rgba(255, 255, 255, 0.95)', 'rgba(255, 255, 255, 0.95)']}
          style={styles.bottomControls}
        >
          <View style={styles.navigationControls}>
            <TouchableOpacity
              style={[styles.navButton, page <= 1 && styles.navButtonDisabled]}
              onPress={prevPage}
              disabled={page <= 1}
            >
              <MaterialCommunityIcons
                name="chevron-left"
                size={28}
                color={page <= 1 ? (isDarkMode ? "#4a5568" : "#9ca3af") : (isDarkMode ? "#ffffff" : "#1f2937")}
              />
            </TouchableOpacity>

            <View style={styles.pageInputContainer}>
              <TextInput
                style={[styles.pageInput, { 
                  color: isDarkMode ? "#ffffff" : "#1f2937", 
                  borderColor: isDarkMode ? "#4a5568" : "#d1d5db",
                  backgroundColor: isDarkMode ? '#2d3748' : '#ffffff'
                }]}
                value={page.toString()}
                onChangeText={(text) => {
                  const num = parseInt(text);
                  if (!isNaN(num)) {
                    goToPage(num);
                  }
                }}
                keyboardType="number-pad"
                maxLength={4}
              />
              <Text style={[styles.pageTotal, { color: isDarkMode ? "#a0aec0" : "#6b7280" }]}>
                / {totalPages || '?'}
              </Text>
            </View>

            <TouchableOpacity
              style={[styles.navButton, page >= totalPages && styles.navButtonDisabled]}
              onPress={nextPage}
              disabled={page >= totalPages}
            >
              <MaterialCommunityIcons
                name="chevron-right"
                size={28}
                color={page >= totalPages ? (isDarkMode ? "#4a5568" : "#9ca3af") : (isDarkMode ? "#ffffff" : "#1f2937")}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.actionControls}>
            <TouchableOpacity
              style={styles.actionButton}
              onPress={downloadPDF}
            >
              <MaterialCommunityIcons
                name="download"
                size={20}
                color={isDarkMode ? "#ffffff" : "#1f2937"}
              />
              <Text style={[styles.actionButtonText, { color: isDarkMode ? "#ffffff" : "#1f2937" }]}>
                Download
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => setShowNotes(!showNotes)}
            >
              <MaterialCommunityIcons
                name={showNotes ? "note-text" : "note-outline"}
                size={20}
                color={isDarkMode ? "#ffffff" : "#1f2937"}
              />
              <Text style={[styles.actionButtonText, { color: isDarkMode ? "#ffffff" : "#1f2937" }]}>
                Notes
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={() => setShowBookmarks(!showBookmarks)}
            >
              <MaterialCommunityIcons
                name={showBookmarks ? "bookmark-multiple" : "bookmark-multiple-outline"}
                size={20}
                color={isDarkMode ? "#ffffff" : "#1f2937"}
              />
              <Text style={[styles.actionButtonText, { color: isDarkMode ? "#ffffff" : "#1f2937" }]}>
                Bookmarks
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={openInBrowser}
            >
              <MaterialCommunityIcons
                name="open-in-new"
                size={20}
                color={isDarkMode ? "#ffffff" : "#1f2937"}
              />
              <Text style={[styles.actionButtonText, { color: isDarkMode ? "#ffffff" : "#1f2937" }]}>
                Browser
              </Text>
            </TouchableOpacity>
          </View>
        </LinearGradient>
      </>
    );
  };

  const renderNotesPanel = () => {
    if (!showNotes) return null;

    return (
      <View style={[styles.panel, { 
        backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
        top: Platform.OS === 'ios' ? 100 : 80,
        right: 16,
        width: 300,
      }]}>
        <View style={styles.panelHeader}>
          <Text style={[styles.panelTitle, { color: isDarkMode ? '#ffffff' : '#1f2937' }]}>
            📝 Notes for Page {page}
          </Text>
          <TouchableOpacity onPress={() => setShowNotes(false)}>
            <MaterialCommunityIcons name="close" size={24} color={isDarkMode ? "#a0aec0" : "#6b7280"} />
          </TouchableOpacity>
        </View>

        <View style={styles.noteInputContainer}>
          <TextInput
            style={[styles.noteInput, { 
              color: isDarkMode ? '#ffffff' : '#1f2937',
              backgroundColor: isDarkMode ? '#4a5568' : '#f3f4f6',
              borderColor: isDarkMode ? '#4a5568' : '#d1d5db'
            }]}
            placeholder="Add a note for this page..."
            placeholderTextColor={isDarkMode ? '#a0aec0' : '#6b7280'}
            value={currentNote}
            onChangeText={setCurrentNote}
            multiline
            numberOfLines={4}
          />
          <TouchableOpacity
            style={[styles.saveButton, !currentNote.trim() && styles.saveButtonDisabled]}
            onPress={saveNote}
            disabled={!currentNote.trim()}
          >
            <Text style={styles.saveButtonText}>Save Note</Text>
          </TouchableOpacity>
        </View>

        {notes[page] && (
          <View style={styles.currentNoteContainer}>
            <Text style={[styles.sectionTitle, { color: isDarkMode ? '#ffffff' : '#1f2937' }]}>
              Your Note:
            </Text>
            <View style={[styles.currentNote, { backgroundColor: isDarkMode ? '#4a5568' : '#f3f4f6' }]}>
              <Text style={[styles.currentNoteText, { color: isDarkMode ? '#ffffff' : '#1f2937' }]}>
                {notes[page]}
              </Text>
              <TouchableOpacity onPress={() => deleteNote(page)}>
                <MaterialCommunityIcons name="delete" size={20} color="#ef4444" />
              </TouchableOpacity>
            </View>
          </View>
        )}

        {Object.keys(notes).length > 0 && (
          <View style={styles.allNotesContainer}>
            <Text style={[styles.sectionTitle, { color: isDarkMode ? '#ffffff' : '#1f2937' }]}>
              All Notes ({Object.keys(notes).length})
            </Text>
            <ScrollView style={styles.notesList} showsVerticalScrollIndicator={false}>
              {Object.entries(notes).map(([notePage, noteText]) => (
                <TouchableOpacity
                  key={notePage}
                  style={[styles.noteItem, { backgroundColor: isDarkMode ? '#4a5568' : '#f3f4f6' }]}
                  onPress={() => jumpToBookmark(parseInt(notePage))}
                >
                  <View style={styles.noteItemHeader}>
                    <Text style={[styles.noteItemPage, { color: isDarkMode ? '#a0aec0' : '#6b7280' }]}>
                      Page {notePage}
                    </Text>
                    <TouchableOpacity onPress={() => deleteNote(parseInt(notePage))}>
                      <MaterialCommunityIcons name="delete-outline" size={16} color="#ef4444" />
                    </TouchableOpacity>
                  </View>
                  <Text style={[styles.noteItemText, { color: isDarkMode ? '#ffffff' : '#1f2937' }]} numberOfLines={2}>
                    {noteText}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        )}
      </View>
    );
  };

  const renderBookmarksPanel = () => {
    if (!showBookmarks) return null;

    return (
      <View style={[styles.panel, { 
        backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
        top: Platform.OS === 'ios' ? 100 : 80,
        left: 16,
        width: 200,
      }]}>
        <Text style={[styles.panelTitle, { color: isDarkMode ? '#ffffff' : '#1f2937' }]}>
          📖 Bookmarks ({bookmarkedPages.length})
        </Text>
        <ScrollView style={styles.bookmarksList} showsVerticalScrollIndicator={false}>
          {bookmarkedPages.map((bookmarkPage) => (
            <TouchableOpacity
              key={bookmarkPage}
              style={[styles.bookmarkItem, { backgroundColor: isDarkMode ? '#4a5568' : '#f3f4f6' }]}
              onPress={() => jumpToBookmark(bookmarkPage)}
            >
              <MaterialCommunityIcons name="bookmark" size={16} color="#f59e0b" />
              <Text style={[styles.bookmarkText, { color: isDarkMode ? '#ffffff' : '#1f2937' }]}>
                Page {bookmarkPage}
              </Text>
              {bookmarkPage === page && (
                <View style={styles.currentPageIndicator}>
                  <Text style={styles.currentPageText}>Current</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  const renderNativePdf = () => {
    if (!Pdf || !directPdfUrl) return null;
    return (
      <View style={{ flex: 1 }}>
        <Pdf
          source={{ uri: directPdfUrl, cache: true }}
          trustAllCerts={false}
          style={styles.webview}
          onLoadComplete={(numberOfPages) => {
            setPdfPageCount(numberOfPages);
            setLoading(false);
          }}
          onPageChanged={(page) => setPdfPage(page)}
          onError={(err) => {
            console.error('Native PDF error:', err);
            setError('Failed to load PDF. Tap retry, or try again on a stronger connection.');
            setLoading(false);
          }}
          onLoadProgress={() => { /* streaming */ }}
          enablePaging={false}
          horizontal={false}
          spacing={6}
          renderActivityIndicator={() => (
            <View style={[styles.loadingContainer, { backgroundColor: isDarkMode ? '#1a202c' : '#f0f4f8' }]}>
              <ActivityIndicator size={36} color="#667eea" />
            </View>
          )}
        />
        {pdfPageCount > 0 && (
          <View style={styles.pdfPageBadge}>
            <Text style={styles.pdfPageBadgeText}>{pdfPage} / {pdfPageCount}</Text>
          </View>
        )}
      </View>
    );
  };

  const renderWebView = () => {
    if (!pdfUri) return null;

    return (
      <WebView
        ref={webViewRef}
        source={{ uri: pdfUri || undefined }}
        style={styles.webview}
        onLoadStart={() => setLoading(true)}
        onLoadEnd={() => setLoading(false)}
        onError={(error) => {
          console.error('WebView error:', error);
          setError('Failed to load PDF');
          setLoading(false);
        }}
        startInLoadingState={true}
        renderLoading={() => (
          <View style={[styles.loadingContainer, { backgroundColor: isDarkMode ? '#1a202c' : '#f0f4f8' }]}>
            <ActivityIndicator size={36} color="#667eea" />
          </View>
        )}
        allowsFullscreenVideo
        javaScriptEnabled={true}
        domStorageEnabled={true}
        scalesPageToFit={true}
        onMessage={handleWebViewMessage}
        injectedJavaScript={`
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
          script.onload = function() {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: 'pdfLoaded', success: true })
            );
          };
          document.head.appendChild(script);
          
          let currentPage = 1;
          window.addEventListener('scroll', function() {
            const scrollTop = window.scrollY;
            const pageHeight = window.innerHeight;
            const newPage = Math.floor(scrollTop / pageHeight) + 1;
            
            if (newPage !== currentPage) {
              currentPage = newPage;
              window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: 'pageChange', page: currentPage })
              );
            }
          });
          
          setTimeout(function() {
            const totalHeight = document.documentElement.scrollHeight;
            const pageHeight = window.innerHeight;
            const totalPages = Math.ceil(totalHeight / pageHeight);
            
            window.ReactNativeWebView.postMessage(
              JSON.stringify({ type: 'pageCount', count: totalPages })
            );
          }, 1000);
          
          true;
        `}
      />
    );
  };

  return (
    <SafeAreaView style={[styles.safeArea, { backgroundColor: isDarkMode ? '#1a202c' : '#ffffff' }]}>
      <StatusBar 
        barStyle={isDarkMode ? "light-content" : "dark-content"} 
        backgroundColor={isDarkMode ? "#2d3748" : "#ffffff"} 
      />
      
      <TouchableOpacity 
        style={styles.container} 
        activeOpacity={1}
        onPress={toggleControls}
      >
        {loading && renderLoading()}
        
        {error && renderError()}
        
        {!loading && !error && (
          (Pdf && directPdfUrl) ? renderNativePdf() : renderWebView()
        )}
        
        {renderControls()}
        {renderNotesPanel()}
        {renderBookmarksPanel()}
        
        {!showControls && (
          <TouchableOpacity 
            style={styles.floatingButton}
            onPress={toggleControls}
          >
            <LinearGradient
              colors={['#667eea', '#764ba2']}
              style={styles.floatingButtonInner}
            >
              <MaterialCommunityIcons name="eye" size={24} color="#ffffff" />
            </LinearGradient>
          </TouchableOpacity>
        )}
      </TouchableOpacity>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
  },
  loadingSubtext: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  errorText: {
    fontSize: 16,
    textAlign: 'center',
    marginVertical: 16,
  },
  retryButton: {
    backgroundColor: '#667eea',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  pickButton: {
    backgroundColor: '#10b981',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  pickButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  webview: {
    flex: 1,
  },
  pdfPageBadge: {
    position: 'absolute',
    bottom: 20,
    alignSelf: 'center',
    backgroundColor: 'rgba(17,24,39,0.82)',
    paddingHorizontal: 14,
    paddingVertical: 6,
    borderRadius: 16,
  },
  pdfPageBadgeText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '600',
  },
  topControls: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    paddingTop: Platform.OS === 'ios' ? 40 : 12,
    zIndex: 1000,
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  titleContainer: {
    flex: 1,
  },
  documentTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  pageInfo: {
    fontSize: 12,
    marginTop: 2,
  },
  topRightControls: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconButton: {
    padding: 8,
    marginLeft: 8,
  },
  bottomControls: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingVertical: 16,
    paddingBottom: Platform.OS === 'ios' ? 30 : 16,
    zIndex: 1000,
  },
  navigationControls: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  navButton: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  navButtonDisabled: {
    opacity: 0.5,
  },
  pageInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pageInput: {
    width: 60,
    height: 40,
    borderWidth: 1,
    borderRadius: 6,
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 'bold',
    marginHorizontal: 8,
  },
  pageTotal: {
    fontSize: 14,
    marginLeft: 4,
  },
  actionControls: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  actionButton: {
    alignItems: 'center',
  },
  actionButtonText: {
    fontSize: 12,
    marginTop: 4,
  },
  panel: {
    position: 'absolute',
    borderRadius: 12,
    padding: 16,
    zIndex: 1001,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    maxHeight: height * 0.7,
  },
  panelHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  panelTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  noteInputContainer: {
    marginBottom: 16,
  },
  noteInput: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
    marginBottom: 12,
  },
  saveButton: {
    backgroundColor: '#667eea',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#9ca3af',
  },
  saveButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  currentNoteContainer: {
    marginBottom: 16,
  },
  currentNote: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 8,
  },
  currentNoteText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    marginRight: 12,
  },
  allNotesContainer: {
    marginTop: 16,
  },
  notesList: {
    maxHeight: 200,
  },
  noteItem: {
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  noteItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  noteItemPage: {
    fontSize: 12,
    fontWeight: '600',
  },
  noteItemText: {
    fontSize: 14,
    lineHeight: 18,
  },
  bookmarksList: {
    maxHeight: 300,
  },
  bookmarkItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderRadius: 8,
    marginBottom: 8,
  },
  bookmarkText: {
    fontSize: 14,
    marginLeft: 8,
    flex: 1,
  },
  currentPageIndicator: {
    backgroundColor: '#667eea',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  currentPageText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: '600',
  },
  floatingButton: {
    position: 'absolute',
    bottom: 100,
    right: 20,
    zIndex: 999,
  },
  floatingButtonInner: {
    width: 56,
    height: 56,
    borderRadius: 28,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
});

export default PDFViewer;